import { payWithRazorpay } from "./payments/razorpayAdapter";

export class UnsupportedGatewayError extends Error {
  constructor(gatewayName) {
    super(`Payment gateway "${gatewayName}" is not supported on web yet.`);
    this.name = "UnsupportedGatewayError";
    this.gatewayName = gatewayName;
  }
}

export const isStripeGateway = (gatewayName) =>
  (gatewayName || "").toLowerCase() === "stripe";

export const isRazorpayGateway = (gatewayName) =>
  (gatewayName || "").toLowerCase() === "razorpay";

export const getStripeClientSecret = (gatewayData, prepareData) => {
  return (
    gatewayData?.client_secret ||
    gatewayData?.stripe_client_secret ||
    prepareData?.client_secret ||
    prepareData?.deposit_summary?.client_secret ||
    ""
  );
};

export const getPublishableKeyFromPrepareData = (gatewayData) => {
  return (
    gatewayData?.publishable_key ||
    gatewayData?.public_payment_key ||
    gatewayData?.razorpay_key ||
    ""
  );
};

export const getStorePaymentConfig = async (endpoint, storeId, { getAPI }) => {
  if (!storeId) return null;
  try {
    const resp = await getAPI(endpoint, `get-store-payment-config/${storeId}`);
    return resp?.data || resp || null;
  } catch (err) {
    console.log("Error fetching payment config:", err);
    return null;
  }
};

const STRIPE_OK_STATUSES = ["requires_capture", "succeeded"];

export const confirmStripePayment = async ({
  stripe,
  elements,
  clientSecret,
  returnUrl,
}) => {
  if (!stripe || !elements) {
    throw new Error("Stripe has not finished loading yet.");
  }
  if (!clientSecret) {
    throw new Error("Missing Stripe client_secret from prepare response");
  }

  const { error: submitError } = await elements.submit();
  if (submitError) {
    throw new Error(submitError.message || "Please check your card details.");
  }

  const { error, paymentIntent } = await stripe.confirmPayment({
    elements,
    clientSecret,
    redirect: "if_required",
    confirmParams: returnUrl ? { return_url: returnUrl } : undefined,
  });

  if (error) {
    throw new Error(error.message || "Payment failed. Please try again.");
  }

  if (!paymentIntent) {
    throw new Error("Stripe returned no payment intent");
  }

  if (!STRIPE_OK_STATUSES.includes(paymentIntent.status)) {
    throw new Error(
      `Payment not ready for booking confirm (status: ${paymentIntent.status}). Complete card entry and try again.`
    );
  }

  return {
    gateway_name: "stripe",
    gateway_auth_intent_id: paymentIntent.id,
    gateway_payment_intent_id: paymentIntent.id,
  };
};

export const payWithGateway = async (gatewayData, opts = {}) => {
  const gatewayName = (gatewayData?.gateway_name || "stripe").toLowerCase();

  if (isRazorpayGateway(gatewayName)) {
    return payWithRazorpay(gatewayData);
  }

  if (isStripeGateway(gatewayName)) {
    const clientSecret =
      opts.clientSecret || getStripeClientSecret(gatewayData, opts.prepareData);
    return confirmStripePayment({
      stripe: opts.stripe,
      elements: opts.elements,
      clientSecret,
      returnUrl: opts.returnUrl,
    });
  }

  throw new UnsupportedGatewayError(gatewayName);
};
